import { useQuery } from "@tanstack/react-query";

import {
  listNotifications,
  useGetNotificationUnreadCount,
} from "@vega-api/generated/notifications-v1/notifications-v1";

import {
  getMetadataUnreadCountParams,
  getMetadataUnreadCountQueryKey,
  type NotificationScope,
} from "./notificationScope";

type Options = {
  enabled?: boolean;
};

/**
 * Live unread count for a scope. A publisher-only scope uses the dedicated
 * `unread-count` endpoint; a metadata-filtered scope has no such endpoint, so
 * it is derived from a 1-row unread list query's `meta.count`.
 */
export const useScopedUnreadCount = (
  scope: NotificationScope,
  { enabled = true }: Options = {},
): number => {
  const isMetadataScoped = Boolean(scope.metadata);

  const publisherCount = useGetNotificationUnreadCount(
    scope.publisher ? { publisher: scope.publisher } : undefined,
    {
      query: {
        enabled: enabled && !isMetadataScoped,
        select: (response) => response.data.count,
      },
    },
  );

  const metadataCount = useQuery({
    queryKey: getMetadataUnreadCountQueryKey(scope),
    queryFn: () => listNotifications(getMetadataUnreadCountParams(scope)),
    select: (response) => response.meta?.count ?? 0,
    enabled: enabled && isMetadataScoped,
  });

  return (isMetadataScoped ? metadataCount.data : publisherCount.data) ?? 0;
};
